import {
  takeLatest, put, select, takeEvery,
} from 'redux-saga/effects';
import uuid from 'uuid';
import ArticleService from '../../../services/ArticleService';
import DocumentService from '../../../services/DocumentService';
import VideoService from '../../../services/VideoService';
import { apiCall, forEachGen } from '../../effects';
import { getErrorNotification, getQueryParamsByItemList } from '../../util';
import { errorNotify, notify } from '../notifications/actions';
import {
  selectArticleForms,
  selectUploadMedia,
  selectArticles,
  selectCurrentDocumentPage,
  selectDocumentPageList,
} from './selectors';
import { selectLists } from '../lists/selectors';
import { getAllQueryData, getExtensionFromMimeType, updateQuery } from '../../../utility/helpers';
import { UploadMediaItem } from './types';
import {
  initArticleRequest,
  initArticleSuccess,
  initArticleFail,
  getArticleRequest,
  getArticleSuccess,
  getArticleFail,
  getArticleListRequest,
  getArticleListSuccess,
  getArticleListFail,
  createArticleRequest,
  createArticleSuccess,
  createArticleFail,
  saveArticleRequest,
  saveArticleSuccess,
  saveArticleFail,
  saveArticleDocumentRequest,
  saveArticleDocumentSuccess,
  saveArticleDocumentFail,
  deleteArticleRequest,
  deleteArticleSuccess,
  deleteArticleFail,
  lockMediaToProjectRequest,
  setArticleFormsReset,
  setArticleFormsSubmit,
  removeArticleForm,
  resetArticleForm,
  submitArticleForm,
  uploadNewArticleMediaRequest,
  uploadArticleMediaSuccess,
  clearArticleList,
  getArticleDocumentDownloadUrlRequest,
  getArticleDocumentDownloadUrlSuccess,
  getArticleDocumentDownloadUrlFail,
  getArticleVideoDownloadUrlRequest,
  getArticleVideoDownloadUrlSuccess,
  getArticleVideoDownloadUrlFail,
  uploadNewArticleVideoRequest,
  getArticleVideoRequest,
  getArticleVideoSuccess,
  getArticleVideoFail,
  saveArticleVideoRequest,
  saveArticleVideoSuccess,
  saveArticleVideoFail,
  setRefreshArticleList,
  getArticleListRefreshRequest,
  getArticleListRefreshSuccess,
  saveVideoThumbnailRequest,
  saveVideoThumbnailSuccess,
  addDocumentDownloadRequest,
  addDownloadMedia,
  setDownloadMedia,
  getArticleDocumentPagesRequest,
  getArticleDocumentPagesSuccess,
  setNextArticleDocumentPage,
  setPreviousArticleDocumentPage,
  setCurrentArticleDocumentPage,
  getArticleDocumentPagesFail,
  setCurrentDocumentPageIndexMediaViewer,
  getArticleVideoMasterRequest,
  getArticleVideoMasterFail,
  clearArticleState,
  getArticleVideoMasterSuccess,
  updateProcessingMediaArticleRequest,
  updateProcessingMediaArticleSuccess,
  updateProcessingMediaArticleFail,
  downloadAllMedia,
  createNewArticleUpload,
  updateNewArticleUpload,
  uploadMediaFromURLRequest,
  uploadMediaFromURLFail,
  uploadMediaFromURLSuccess,
  updateRefVideoRef,
  updateRefDocumentRef,
  removeAllUploadArticleMedia,
} from './actions';
import { logOut } from '../auth/actions';
import { getProjectRequest } from '../projects/actions';
import VideoMenuService from '../../../services/VideoMenuService';
import { getFileExtension } from '../../../utility/string';
import { selectProject } from '../projects/selectors';
import { Article, Scenes } from '../../../types/items';
import {
  ArticlesWithNumbers, findAndDeleteSceneFromSceneScript,
  findArticlesInScene,
  numberingForArticleDownload, OutputDataFromFindArticlesInScene,
  transformDocumentPage,
} from './utils';
import { selectListScenes } from '../scenes/selectors';
import { updateScenesListRequest } from '../scenes/actions';

function* getArticle({ payload }: ReturnType<typeof getArticleRequest>) {
  try {
    if (Number.isNaN(Number(payload))) {
      throw new Error('Invalid article id');
    }
    const response = yield apiCall(ArticleService.get, payload);
    yield put(getArticleSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getArticleFail());
  }
}

function* getArticleList({ payload }: ReturnType<typeof getArticleListRequest>) {
  try {
    const list = yield apiCall(ArticleService.list, payload);
    yield put(getArticleListSuccess(list));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getArticleListFail());
  }
}

function* saveArticle({ payload }: ReturnType<typeof saveArticleRequest>) {
  try {
    const response = yield apiCall(ArticleService.save, payload);
    yield put(saveArticleSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(saveArticleFail());
  }
}

function* saveArticleDocument({ payload }: ReturnType<typeof saveArticleDocumentRequest>) {
  try {
    const response = yield apiCall(DocumentService.save, payload);
    yield put(saveArticleDocumentSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(saveArticleDocumentFail());
  }
}

function* createArticle({ payload }: ReturnType<typeof createArticleRequest>) {
  try {
    const response = yield apiCall(ArticleService.create, payload);
    yield put(createArticleSuccess(response));
  } catch (e) {
    yield put(initArticleSuccess(payload));
    yield put(notify(getErrorNotification(e)));
    yield put(createArticleFail());
  }
}

function* deleteArticle({ payload }: ReturnType<typeof deleteArticleRequest>) {
  try {
    yield apiCall(ArticleService.delete, payload);
    const scenes = yield select(selectListScenes);
    const articles = yield select(selectArticles);
    const foundScenes = findAndDeleteSceneFromSceneScript(scenes.data.items, articles.data.items, payload);
    if (foundScenes.length) {
      yield put(updateScenesListRequest(foundScenes));
    }
    yield put(deleteArticleSuccess(payload));
  } catch (e) {
    console.log(e);
    yield put(notify(getErrorNotification(e)));
    yield put(deleteArticleFail());
  }
}

function* getTemplateArticle() {
  try {
    const response = yield apiCall(ArticleService.template);
    yield put(initArticleSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(initArticleFail());
  }
}

function* lockMediaToProject({ payload }: ReturnType<typeof lockMediaToProjectRequest>) {
  try {
    yield apiCall(ArticleService.save, payload);
    const message = payload.lock_media_to_project ? 'File locked to template' : 'File unlocked to template';
    const icon = payload.lock_media_to_project ? 'lock' : 'unlock';
    yield put(notify({ type: 'success', icon, message }));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
  }
}

function* submitForms({ payload }: ReturnType<typeof submitArticleForm>) {
  try {
    yield put(removeArticleForm(payload.id));
    const forms = yield select(selectArticleForms);
    payload.callback();
    if (forms.forms.length === 0) {
      yield put(setArticleFormsSubmit(false));
    }
  } catch (e) {
    console.log(e);
  }
}

function* resetForms({ payload }: ReturnType<typeof resetArticleForm>) {
  try {
    yield put(removeArticleForm(payload.id));
    payload.callback();
    const forms = yield select(selectArticleForms);
    if (forms.forms.length === 0) {
      yield put(setArticleFormsReset(false));
    }
  } catch (e) {
    console.log(e);
  }
}

function* uploadNewMedia({ payload }: ReturnType<typeof uploadNewArticleMediaRequest>) {
  try {
    const article = yield apiCall(
      ArticleService.createDocument,
      {
        original_filename: payload.title,
        title: '',
        project_id: payload.projectId,
      },
    );
    yield put(createArticleSuccess(article));
    yield put(createNewArticleUpload({
      article,
      uploadId: payload.uploadId,
      signedUrl: null,
    }));
    const uploadUrl = yield apiCall(DocumentService.uploadUrl, article.document_id);
    yield put(updateNewArticleUpload({
      uploadId: payload.uploadId,
      signedUrl: uploadUrl,
    }));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    console.log(e);
  }
}

function* uploadNewVideo({ payload }: ReturnType<typeof uploadNewArticleVideoRequest>) {
  try {
    const article = yield apiCall(
      ArticleService.createVideo,
      {
        title: '', // This field is intentionally empty--the back end will calculate the title based on the filename
        project_id: payload.projectId,
        scene_id: -1,
        local_file_name: payload.title,
        recorded_in_studio: Boolean(payload.recordedInStudio),
      },
    );
    yield put(createArticleSuccess(article));
    yield put(createNewArticleUpload({
      article,
      uploadId: payload.uploadId,
      signedUrl: null,
    }));
    const uploadUrl = yield apiCall(VideoService.uploadUrl, article.video_id);
    yield put(updateNewArticleUpload({
      uploadId: payload.uploadId,
      signedUrl: uploadUrl,
    }));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    console.log(e);
  }
}

function* uploadMediaSuccess({ payload }: ReturnType<typeof uploadArticleMediaSuccess>) {
  const { uploads } = yield select(selectUploadMedia);
  const mediaItem = uploads.find((item: UploadMediaItem) => item.uploadId === payload);

  try {
    if (mediaItem) {
      const { article } = mediaItem;
      const isVideo = article.video_id !== -1;
      if (isVideo) {
        const videoRef = yield apiCall(VideoService.sync, article.video_id);
        yield put(updateRefVideoRef(videoRef));
      } else {
        const document = yield apiCall(DocumentService.sync, article.document_id);
        yield put(updateRefDocumentRef(document));
      }
    }
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    console.log(e);
  }
}

function* clearState() {
  yield put(removeAllUploadArticleMedia());
  yield put(clearArticleState());
}

function* clearList() {
  yield put(clearArticleList());
}

function* documentDownloadUrl({ payload }: ReturnType<typeof getArticleDocumentDownloadUrlRequest>) {
  try {
    const response = yield apiCall(DocumentService.downloadUrl, payload);
    yield put(getArticleDocumentDownloadUrlSuccess({ signedUrl: response, documentId: payload }));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getArticleDocumentDownloadUrlFail());
  }
}

function* getDocumentPages({ payload }: ReturnType<typeof getArticleDocumentDownloadUrlRequest>) {
  try {
    const response = yield apiCall(DocumentService.getPageInfo, payload);
    yield put(getArticleDocumentPagesSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getArticleDocumentPagesFail());
  }
}

function* setNextDocumentPages() {
  const documentPages = yield select(selectDocumentPageList);
  const currentDocumentPage = yield select(selectCurrentDocumentPage);

  if (documentPages && currentDocumentPage && currentDocumentPage.id < documentPages.length) {
    yield put(setCurrentArticleDocumentPage(documentPages[currentDocumentPage.id]));
  }
}

function* setPreviousDocumentPages() {
  const documentPages = yield select(selectDocumentPageList);
  const currentDocumentPage = yield select(selectCurrentDocumentPage);

  if (documentPages && documentPages.length && currentDocumentPage.id > 1) {
    yield put(setCurrentArticleDocumentPage(documentPages[currentDocumentPage.id - 2]));
  }
}

function* videoDownloadUrl({ payload }: ReturnType<typeof getArticleVideoDownloadUrlRequest>) {
  try {
    const response = yield apiCall(VideoService.downloadUrl, payload);
    yield put(getArticleVideoDownloadUrlSuccess({ signedUrl: response, videoId: payload }));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getArticleVideoDownloadUrlFail());
  }
}

function* getVideo({ payload }: ReturnType<typeof getArticleVideoRequest>) {
  try {
    const response = yield apiCall(VideoService.get, payload);
    yield put(getArticleVideoSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getArticleVideoFail());
  }
}

function* getVideoMasterRequest({ payload }: ReturnType<typeof getArticleVideoMasterRequest>) {
  try {
    yield apiCall(VideoMenuService.generateVideoMaster, payload.id);
    const response = yield apiCall(VideoMenuService.getVideoMaster, payload.id);

    if (response.status === 'ready' && response.url) {
      yield put(notify({ type: 'success', message: 'The master file will begin download' }));

      const downloadId = uuid();
      const ext = getFileExtension(response.url);
      const fileName = `${payload.ref.filename_base}.${ext}`;

      yield put(
        addDownloadMedia({
          url: response.url,
          name: fileName,
          downloadId,
          urlRequested: false,
        }),
      );

      yield put(setDownloadMedia({
        url: response.url,
        name: fileName,
        downloadId,
        urlRequested: false,
      }));
    }
    yield put(getArticleVideoMasterSuccess());
  } catch (e) {
    yield put(getArticleVideoMasterFail());
  }
}

function* saveArticleVideo({ payload }: ReturnType<typeof saveArticleVideoRequest>) {
  try {
    const response = yield apiCall(VideoService.save, payload.id, payload);
    yield put(saveArticleVideoSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(saveArticleVideoFail());
  }
}

function* refreshArticleState({ payload }: ReturnType<typeof setRefreshArticleList>) {
  try {
    if (payload) {
      const { data } = yield select(selectArticles);
      if (data !== null) {
        const params = getQueryParamsByItemList(data);
        yield put(getArticleListRefreshRequest(params));
      }
      yield put(setRefreshArticleList(false));
    }
  } catch (e) {
    console.log(e);
  }
}

function* getArticleListRefresh({ payload }: ReturnType<typeof getArticleListRefreshRequest>) {
  try {
    const list = yield apiCall(ArticleService.list, payload);
    yield put(getArticleListRefreshSuccess(list));
  } catch (e) {
    console.log(e);
  }
}

function* saveVideoThumbnail({ payload }: ReturnType<typeof saveVideoThumbnailRequest>) {
  try {
    const response = yield VideoService.save(payload.id, payload);
    yield put(saveVideoThumbnailSuccess(response));
    yield put(
      notify({
        type: 'success',
        message: 'Thumbnail updated.',
      }),
    );
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
  }
}

function* addDocumentDownload({ payload }: ReturnType<typeof addDocumentDownloadRequest>) {
  try {
    const lists = yield select(selectLists);
    if (lists.data !== null) {
      const ext = getExtensionFromMimeType(
        {
          mime: payload.mimeType,
          mimeList: lists.data['document-mime-types'],
        },
      );
      let fileName: string;
      const downloadId = uuid();
      if (payload.url) {
        fileName = payload.title;
        yield put(
          addDownloadMedia({
            name: fileName,
            url: payload.url,
            downloadId,
            urlRequested: true,
          }),
        );
        yield put(
          setDownloadMedia({
            name: fileName,
            url: payload.url,
            downloadId,
            urlRequested: false,
          }),
        );
      } else {
        fileName = `${payload.title}.${ext}`;
        yield put(
          addDownloadMedia({
            name: fileName,
            url: '',
            downloadId,
            urlRequested: true,
          }),
        );
        const response = yield apiCall(DocumentService.downloadUrl, payload.documentId);
        yield put(
          setDownloadMedia({
            name: fileName,
            url: response.url,
            downloadId,
            urlRequested: false,
          }),
        );
      }
    }
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
  }
}

function* updateDocumentPageIndexQueryData({ payload }: ReturnType<typeof setCurrentDocumentPageIndexMediaViewer>) {
  try {
    const queryData = getAllQueryData();
    if (payload > 1 && queryData.documentPageId !== payload) {
      queryData.documentPageId = payload;
    }
    if (payload === 1) {
      delete queryData.documentPageId;
    }
    updateQuery(queryData);
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
  }
}

function* updateCurrentArticleDocumentPage({ payload }: ReturnType<typeof setCurrentArticleDocumentPage>) {
  yield put(setCurrentDocumentPageIndexMediaViewer(payload.id));
}

function* updateProcessingMediaArticle({ payload }: ReturnType<typeof updateProcessingMediaArticleRequest>) {
  try {
    if (Number.isNaN(Number(payload.id))) {
      throw new Error('Invalid article id');
    }
    const response = yield apiCall(ArticleService.get, payload.id);
    if (response) {
      yield put(updateProcessingMediaArticleSuccess(response));
    }
  } catch (e) {
    yield put(errorNotify('An Error Occurred'));
    yield put(updateProcessingMediaArticleFail());
  }
}

function* createDataForDownloading(outputDataFromFindArticles:OutputDataFromFindArticlesInScene []) {
  const findDocumentsArr = outputDataFromFindArticles.filter((item) => (item as ArticlesWithNumbers).pageNumber);
  const articlesWithSameDocuments = findDocumentsArr.reduce((accum: {[key: number]: ArticlesWithNumbers[]}, current) => {
    const documentId = (current as ArticlesWithNumbers).article.document_id;
    if (!accum[documentId]) {
      accum[documentId] = [];
    }
    accum[documentId].push(current as ArticlesWithNumbers);
    return { ...accum };
  }, {});

  const dataForDownloadRequest = outputDataFromFindArticles.map((item) => {
    if ((item as ArticlesWithNumbers).pageNumber) {
      return {
        url: '',
        documentId: (item as ArticlesWithNumbers).article.document_id,
        mimeType: (item as ArticlesWithNumbers).article.ref.document!.mime_type_id,
        title: '',
      };
    }
    if ((item as Article).ref.video?.ref.statics) {
      return {
        url: (item as Article).ref.video!.ref.statics[(item as Article).ref.video!.ref.statics.length - 1].url,
        name: (item as Article).ref.video!.title,
        downloadId: uuid(),
        urlRequested: false,
      };
    }
    return {
      documentId: (item as Article).document_id,
      mimeType: (item as Article).ref.document!.mime_type_id,
      title: (item as Article).ref.document!.title,
      mimeId: (item as Article).ref.document?.media_type_id,
    };
  });

  function* processDocuments([documentId, articlesWithNumbers]:[string, ArticlesWithNumbers[]]) {
    const pages = yield apiCall(DocumentService.getPageInfo, Number(documentId));
    const indexesOfDataForDownloadRequest = dataForDownloadRequest.reduce((accum: number[], current, i) => {
      if (current.documentId === articlesWithNumbers[0].article.document_id) {
        return [...accum, i];
      }
      return accum;
    }, []);
    articlesWithNumbers.forEach(({ pageNumber }, i) => {
      const page = transformDocumentPage(pages)[pageNumber - 1];
      // @ts-ignore
      dataForDownloadRequest[indexesOfDataForDownloadRequest[i]] = { ...dataForDownloadRequest[indexesOfDataForDownloadRequest[i]], url: page.url.url, title: page.filename };
    });
    dataForDownloadRequest.push({
      documentId: articlesWithNumbers[0].article.document_id,
      mimeType: articlesWithNumbers[0].article.ref.document!.mime_type_id,
      title: articlesWithNumbers[0].article.ref.document!.title,
      mimeId: articlesWithNumbers[0].article.ref.document?.media_type_id,
    });
  }

  yield forEachGen(Object.entries(articlesWithSameDocuments), processDocuments);
  return dataForDownloadRequest;
}

function* downloadAllMediaHandler({ payload }: ReturnType<typeof downloadAllMedia>) {
  function* processMedia(data: any) {
    const item = data.val ? data.val : data;
    console.log(item);
    if (item.id) {
      if (item.ref.document) {
        yield put(addDocumentDownloadRequest({
          documentId: item.document_id,
          mimeType: item.ref.document.mime_type_id,
          title: item.ref.document.title,
        }));
      } else if (item.ref.video?.ref.statics) {
        yield put(addDownloadMedia({
          url: item.ref.video.ref.statics[item.ref.video.ref.statics.length - 1].url,
          name: item.ref.video?.title || 'Video',
          downloadId: uuid(),
          urlRequested: false,
        }));
      }
    } else if (item.downloadId) {
      const documentData = payload?.isNumbering
        ? { ...item, name: `${numberingForArticleDownload(data.index + 1)}-${item.name}` } : item;
      yield put(addDownloadMedia(documentData));
    } else {
      const documentData = payload?.isNumbering && item.mimeId !== 4
        ? { ...item, title: `${numberingForArticleDownload(data.index + 1)}-${item.title}` } : item;
      yield put(addDocumentDownloadRequest(documentData));
    }
  }
  try {
    const project = yield select(selectProject);
    const response = yield apiCall(ArticleService.list, {
      page_size: 1000000,
      filter: {
        project_id: project.data.id,
        is_media: 1,
      },
    });
    if (!payload) {
      yield forEachGen(response.items, processMedia);
    } else {
      const scenes = yield select(selectListScenes);
      const foundScene = scenes.data.items.find((scene: Scenes) => scene.id === payload.sceneId);
      if (!foundScene) throw new Error('Scene not found');
      const foundArticles = findArticlesInScene(foundScene, response.items as Article[]);
      const data = yield* createDataForDownloading(foundArticles);
      yield forEachGen(data, processMedia, true);
    }
  } catch (e) {
    console.error(e);
  }
}

function* uploadMediaFromUrl({ payload }: ReturnType<typeof uploadMediaFromURLRequest>) {
  try {
    yield apiCall(ArticleService.uploadVideoFromAnotherResource, {
      title: payload.title,
      project_id: payload.project_id,
      url: payload.url,
    });

    yield put(uploadMediaFromURLSuccess());
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(uploadMediaFromURLFail());
  }
}

export default [
  takeLatest(initArticleRequest.type, getTemplateArticle),
  takeEvery(getArticleRequest.type, getArticle),
  takeLatest(getArticleListRequest.type, getArticleList),
  takeLatest(createArticleRequest.type, createArticle),
  takeEvery(saveArticleRequest.type, saveArticle),
  takeEvery(deleteArticleRequest.type, deleteArticle),
  takeEvery(lockMediaToProjectRequest.type, lockMediaToProject),
  takeLatest(submitArticleForm.type, submitForms),
  takeLatest(resetArticleForm.type, resetForms),
  takeLatest(logOut.type, clearState),
  takeLatest(getProjectRequest.type, clearList),
  takeLatest(getArticleDocumentDownloadUrlRequest.type, documentDownloadUrl),
  takeLatest(getArticleDocumentPagesRequest.type, getDocumentPages),
  takeLatest(setNextArticleDocumentPage.type, setNextDocumentPages),
  takeLatest(setPreviousArticleDocumentPage.type, setPreviousDocumentPages),
  takeLatest(getArticleVideoDownloadUrlRequest.type, videoDownloadUrl),
  takeEvery(getArticleVideoMasterRequest.type, getVideoMasterRequest),
  takeEvery(getArticleVideoRequest.type, getVideo),
  takeEvery(saveArticleDocumentRequest.type, saveArticleDocument),
  takeEvery(saveArticleVideoRequest.type, saveArticleVideo),
  takeEvery(uploadNewArticleMediaRequest.type, uploadNewMedia),
  takeEvery(uploadArticleMediaSuccess.type, uploadMediaSuccess),
  takeEvery(uploadNewArticleVideoRequest.type, uploadNewVideo),
  takeLatest(setRefreshArticleList.type, refreshArticleState),
  takeLatest(getArticleListRefreshRequest.type, getArticleListRefresh),
  takeLatest(saveVideoThumbnailRequest.type, saveVideoThumbnail),
  takeEvery(addDocumentDownloadRequest.type, addDocumentDownload),
  takeEvery(setCurrentDocumentPageIndexMediaViewer.type, updateDocumentPageIndexQueryData),
  takeEvery(setCurrentArticleDocumentPage.type, updateCurrentArticleDocumentPage),
  takeEvery(updateProcessingMediaArticleRequest.type, updateProcessingMediaArticle),
  takeLatest(downloadAllMedia.type, downloadAllMediaHandler),
  takeLatest(uploadMediaFromURLRequest.type, uploadMediaFromUrl),
];
